import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../lib/axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Plus, Package, DollarSign, TrendingDown, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { HelpTooltip, TitleWithHelp } from '../components/ui/help-tooltip';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeader = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('nautilus_token')}` }
});

const RawMaterials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    supplier: '',
    unit_cost: '',
    minimum_stock: '',
    current_stock: '',
    unit: ''
  });

  useEffect(() => {
    document.title = "Matéria-Prima — NautilusOS";
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await api.get('/raw-materials');
      setMaterials(response.data);
    } catch (error) {
      toast.error('Erro ao carregar materiais');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/raw-materials`, {
        ...formData,
        unit_cost: parseFloat(formData.unit_cost),
        minimum_stock: parseFloat(formData.minimum_stock),
        current_stock: parseFloat(formData.current_stock)
      });
      toast.success('Material cadastrado com sucesso!');
      setShowDialog(false);
      setFormData({
        name: '',
        supplier: '',
        unit_cost: '',
        minimum_stock: '',
        current_stock: '',
        unit: ''
      });
      fetchMaterials();
    } catch (error) {
      toast.error('Erro ao cadastrar material');
    }
  };

  const updateStock = async (materialId, newStock) => {
    try {
      await api.put(`/raw-materials/${materialId}`, {
        current_stock: parseFloat(newStock)
      });
      toast.success('Estoque atualizado!');
      fetchMaterials();
    } catch (error) {
      toast.error('Erro ao atualizar estoque');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const lowStockMaterials = materials.filter(m => m.current_stock < m.minimum_stock);

  return (
    <div className="space-y-6" data-testid="raw-materials-page">
      <div className="flex items-center justify-between">
        <div>
          <TitleWithHelp
            title="Matéria-Prima"
            description="Controle de estoque de insumos. Monitore níveis, custos e receba alertas quando o estoque estiver baixo. Vinculado aos fornecedores cadastrados."
            connections={['Fornecedores']}
          >
            <h1 className="text-3xl font-bold text-gray-800">Gestão de Matéria-Prima</h1>
          </TitleWithHelp>
          <p className="text-gray-600 mt-1">Controle de estoque e insumos</p>
        </div>
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogTrigger asChild>
            <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-material-button">
              <Plus className="mr-2" size={20} />
              Novo Material
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Cadastrar Material</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Nome do Material</label>
                  <Input
                    data-testid="material-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Fornecedor</label>
                  <Input
                    data-testid="material-supplier-input"
                    value={formData.supplier}
                    onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Custo Unitário (R$)</label>
                  <Input
                    data-testid="material-cost-input"
                    type="number"
                    step="0.01"
                    value={formData.unit_cost}
                    onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Unidade</label>
                  <Input
                    data-testid="material-unit-input"
                    value={formData.unit}
                    placeholder="kg, litros, unidades"
                    onChange={(e) => setFormData({ ...formData, unit: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Estoque Mínimo</label>
                  <Input
                    data-testid="material-min-stock-input"
                    type="number"
                    step="0.01"
                    value={formData.minimum_stock}
                    onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Estoque Atual</label>
                  <Input
                    data-testid="material-current-stock-input"
                    type="number"
                    step="0.01"
                    value={formData.current_stock}
                    onChange={(e) => setFormData({ ...formData, current_stock: e.target.value })}
                    required
                  />
                </div>
              </div>
              <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" data-testid="create-material-submit">
                Cadastrar Material
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {lowStockMaterials.length > 0 && (
        <Card className="border-2 border-red-200 bg-red-50" data-testid="low-stock-alert">
          <CardHeader>
            <CardTitle className="flex items-center text-red-800">
              <AlertTriangle className="mr-2" size={24} />
              Alerta: Materiais com Estoque Baixo
              <HelpTooltip 
                title="Alerta de Estoque"
                description="Materiais que estão abaixo do estoque mínimo configurado. Considere fazer novas compras junto aos fornecedores."
                connections={['Fornecedores']}
                iconSize={16}
                className="ml-2"
              />
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-red-700 mb-2">
              {lowStockMaterials.length} {lowStockMaterials.length === 1 ? 'material está' : 'materiais estão'} abaixo do estoque mínimo:
            </p>
            <ul className="list-disc list-inside space-y-1">
              {lowStockMaterials.map(m => (
                <li key={m.id} className="text-sm text-red-700">
                  <strong>{m.name}</strong>: {m.current_stock} {m.unit} (mínimo: {m.minimum_stock} {m.unit})
                </li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {materials.map(material => {
          const stockPercentage = (material.current_stock / material.minimum_stock) * 100;
          const isLowStock = stockPercentage < 100;
          
          return (
            <Card 
              key={material.id} 
              className={`hover:shadow-lg transition-shadow ${isLowStock ? 'border-red-300' : ''}`}
              data-testid={`material-card-${material.id}`}
            >
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-lg">{material.name}</CardTitle>
                    <p className="text-sm text-gray-600 mt-1">{material.supplier}</p>
                  </div>
                  {isLowStock && (
                    <Badge className="bg-red-100 text-red-800">
                      Estoque Baixo
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center text-sm text-gray-600">
                  <DollarSign size={16} className="mr-2 text-green-600" />
                  R$ {material.unit_cost.toFixed(2)} / {material.unit}
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Estoque</span>
                    <span className="text-sm text-gray-600">
                      {material.current_stock} / {material.minimum_stock} {material.unit}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className={`h-2 rounded-full transition-all ${
                        stockPercentage >= 100 ? 'bg-green-500' : stockPercentage >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(stockPercentage, 100)}%` }}
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Novo estoque"
                    data-testid={`update-stock-input-${material.id}`}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && e.target.value) {
                        updateStock(material.id, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    className="flex-1"
                  />
                  <Button
                    size="sm"
                    onClick={(e) => {
                      const input = e.target.closest('.space-y-4').querySelector('input');
                      if (input.value) {
                        updateStock(material.id, input.value);
                        input.value = '';
                      }
                    }}
                    data-testid={`update-stock-button-${material.id}`}
                  >
                    Atualizar
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default RawMaterials;
